// Display formatting for the plain ISO date strings ("2026-11-15") the project-plan endpoints
// return. Kept as string surgery rather than `new Date()` on purpose: these values carry no time
// or zone, and parsing them would shift the day across the local offset.

export const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Turn an ISO date ("2026-11-15") into a Date at local midnight, for the date pickers that need
 * a Date object. Built from parts rather than `new Date(iso)`, which parses the string as UTC
 * midnight and reads back as the previous day anywhere west of UTC. Returns null when unparseable.
 */
export function parseIsoDate(iso) {
    if (!iso) return null;
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
}

/**
 * Format an ISO date ("2026-11-15") as "15 Nov 2026". Falls back to the raw value if unparseable.
 */
export function formatDateLong(iso) {
    if (!iso) return '';
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return iso;
    return `${parseInt(m[3], 10)} ${MONTH_ABBR[parseInt(m[2], 10) - 1]} ${m[1]}`;
}

/**
 * Format an ISO date ("2026-09-15") as "15 Sep '26". Falls back to the raw value if unparseable.
 */
export function formatDateShort(iso) {
    if (!iso) return '';
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return iso;
    return `${parseInt(m[3], 10)} ${MONTH_ABBR[parseInt(m[2], 10) - 1]} '${m[1].slice(2)}`;
}

const PLAIN_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The API sends a plain date carrying no time or zone, and `new Date()` parses
 * that form as UTC midnight — which the picker and the local getters both read
 * back as the previous day at any negative offset. Building the Date from its
 * parts pins it to local midnight instead, so the day survives the round trip
 * whatever the viewer's zone.
 */
export function toLocalDate(value) {
    const parts = typeof value === 'string' ? PLAIN_DATE.exec(value) : null;

    if (!parts) {
        return '';
    }

    return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
}

/**
 * A picker's Date (or an already plain date) as the plain date the API takes,
 * or null.
 */
export function toDateString(value) {
    if (!value) {
        return null;
    }

    // A string here has bypassed the picker entirely; only a plain date is
    // meaningful, and it is already in the wire format.
    if (typeof value === 'string') {
        return PLAIN_DATE.test(value) ? value : null;
    }

    const pad = (part) => String(part).padStart(2, '0');

    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}
